import { useAuth, getUserAvatarUrl } from '@/contexts/AuthContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { featureService, WorkspaceInvitation } from '@common/api/featureService';
import { CircleNotch, WarningCircle } from '@phosphor-icons/react';
import React, { useState } from 'react';

interface InviteSelectionProps {
  invitations: WorkspaceInvitation[];
  onChooseCreate?: () => void;
}

const InviteSelection: React.FC<InviteSelectionProps> = ({ invitations, onChooseCreate }) => {
    const { startOnboarding } = useOnboarding();
    const { switchWorkspace, user } = useAuth();
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const userAvatarUrl = getUserAvatarUrl(user);

    const handleCreateWorkspace = () => {
        console.log('点击创建 workspace');
        // 通知父组件用户选择了创建 workspace
        onChooseCreate?.();
        // 开始 onboarding 流程
        startOnboarding();
    };

    const handleJoin = async (invitation: WorkspaceInvitation) => {
        setAcceptingId(invitation.id);
        setError(null);

        try {
            console.log('接受邀请:', invitation);

            // 调用接受邀请的 API
            const response = await featureService.acceptInvitation(invitation.token);

            console.log('接受邀请 API 响应:', response);

            // 切换到刚加入的 workspace
            switchWorkspace(response.workspaceId);
            
        } catch (err: any) {
            console.error('接受邀请失败:', err);
            setError(err?.message || 'Failed to accept invitation. Please try again.');
        } finally {
            setAcceptingId(null);
        }
    };

    if (invitations.length === 0) {
        return null;
    }

    return (
        <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
                    <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                        {/* User Avatar */}
                        <img
                            src={userAvatarUrl}
                            alt="User"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <h1 className="text-2xl font-bold text-text-main mb-2">
                        Join teammates or create a workspace
                    </h1>
                    <p className="text-text-sub">
                        You've been invited to 
                        {' '}
                        {invitations.length}
                        {' '}
                        workspace
                        {invitations.length > 1 ? 's' : ''}
                    </p>
                </div>

                {/* Error Message */}
                {error ? (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <WarningCircle size={20} className="shrink-0" weight="bold" />
                        <p className="text-sm">{error}</p>
                    </div>
                ) : null}

                {/* Invites Section */}
                <div className="mb-6">
                    <h2 className="text-xs font-semibold text-text-sub mb-2">
                        Invites
                    </h2>
                    <div className="space-y-3">
                        {invitations.map((invite) => (
                            <div
                                key={invite.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between group hover:border-gray-300 transition-colors"
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-lg font-bold text-text-main shrink-0">
                                        {invite.workspaceName?.charAt(0).toUpperCase() || 'W'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-text-main truncate">{invite.workspaceName}</div>
                                        <div className="text-sm text-text-sub flex items-center gap-1 truncate">
                                            Invited by
                                            {' '}
                                            {invite.inviterName || invite.invitedBy}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleJoin(invite)}
                                    disabled={acceptingId === invite.id}
                                    className="px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {acceptingId === invite.id ? (
                                        <>
                                            <CircleNotch size={16} className="animate-spin" weight="bold" />
                                            Joining...
                                        </>
                                    ) : (
                                        'Join'
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Create Workspace Button */}
                <button
                    onClick={handleCreateWorkspace}
                    className="w-full py-3 bg-primary hover:bg-primary/90 text-white font-medium rounded-xl shadow-sm transition-all flex items-center justify-center gap-2"
                >
                    Create workspace
                </button>
        </div>
    );
};

export default InviteSelection;
